import { Col, Divider, Row } from "antd";
import cuid from "cuid";
import { Formik } from "formik";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useQuery } from "shared/hooks/useApi";
import Card from "shared/components/Card";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { PROCESS_CONFIG_TYPES } from "utils/constants";
import { bindInputProps } from "utils/helpers/input";

const Needs = ({ formRef, dirtyRef, setIsDirty, TABS, onSubmit }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const FORM = t("FORM", { returnObjects: true });

  const { data: coverageCategoriesData } = useQuery(
    graphql.queries.COVERAGE_CATEGORIES,
    {
      variables: {
        gt: { weight: 0 },
      },
    },
  );

  const coverageCategories =
    coverageCategoriesData?.coverageCategories?.data || [];

  return (
    <Formik
      innerRef={(formik) => {
        formRef.current = formik;
      }}
      initialValues={{
        needsLevels: null,
        selectedCategories: [],
        personalizeText: false,
        categories: {},
        labels: {},
      }}
      onSubmit={async (values) => {
        // Build categories object with only selected categories
        const categoriesData = {};
        if (values.selectedCategories.length > 0) {
          values.selectedCategories.forEach((categoryId) => {
            categoriesData[categoryId] = {};
            // If personalizeText is checked, use the values from form, otherwise empty strings
            if (values.personalizeText && values.categories[categoryId]) {
              // Use existing values
              categoriesData[categoryId] = { ...values.categories[categoryId] };
            }
            // Ensure all levels are present (only if needsLevels is set)
            if (values.needsLevels) {
              for (let i = 1; i <= values.needsLevels; i++) {
                if (!categoriesData[categoryId][String(i)]) {
                  categoriesData[categoryId][String(i)] = "";
                }
              }
            }
          });
        }

        // Build labels object
        const labelsData = {};
        if (values.needsLevels && values.labels) {
          for (let i = 1; i <= values.needsLevels; i++) {
            labelsData[String(i)] = values.labels[String(i)] || "";
          }
        }

        const needsConfig =
          values.needsLevels || values.selectedCategories.length > 0
            ? [
                {
                  id: cuid(),
                  type: PROCESS_CONFIG_TYPES.NEEDS,
                  value: {
                    needsLevels: values.needsLevels ?? null,
                    categories: categoriesData,
                    labels: labelsData,
                  },
                },
              ]
            : [];

        await onSubmit({
          type: "",
          name: "",
          theme: null,
          description: "",
          skills: [],
          config: needsConfig,
          attributionType: null,
          processStatuses: [],
          insurancePlans: [],
        });
        setIsDirty((prev) => ({ ...prev, [TABS.NEEDS]: false }));
      }}
    >
      {({ values, dirty, setFieldValue, ...formProps }) => {
        if (dirtyRef.current !== dirty) {
          dirtyRef.current = dirty;
          setIsDirty((prev) => ({ ...prev, [TABS.NEEDS]: dirty }));
        }

        // Initialize categories when selectedCategories changes
        const handleCategoriesChange = (selectedIds) => {
          setFieldValue("selectedCategories", selectedIds);
          // Initialize categories object for new selections
          const updatedCategories = { ...values.categories };
          selectedIds.forEach((categoryId) => {
            if (!updatedCategories[categoryId]) {
              updatedCategories[categoryId] = {};
              // Initialize all levels (only if needsLevels is set)
              if (values.needsLevels) {
                for (let i = 1; i <= values.needsLevels; i++) {
                  if (!updatedCategories[categoryId][String(i)]) {
                    updatedCategories[categoryId][String(i)] = "";
                  }
                }
              }
            }
          });
          // Remove unselected categories
          Object.keys(updatedCategories).forEach((categoryId) => {
            if (!selectedIds.includes(categoryId)) {
              delete updatedCategories[categoryId];
            }
          });
          setFieldValue("categories", updatedCategories);
        };

        // Update only the displayed value (onChange)
        const handleNeedsLevelsInputChange = (inputValue) => {
          const numValue =
            inputValue === "" || inputValue === null
              ? null
              : parseInt(inputValue) || null;
          setFieldValue("needsLevels", numValue);
        };

        // Apply data conservation logic when field loses focus (onBlur)
        const handleNeedsLevelsBlur = () => {
          formProps.setFieldTouched("needsLevels", true);
          const numValue = values.needsLevels;
          if (numValue === null || numValue === undefined) {
            return;
          }

          const updatedCategories = { ...values.categories };
          const updatedLabels = { ...values.labels };

          if (numValue) {
            values.selectedCategories.forEach((categoryId) => {
              if (!updatedCategories[categoryId]) {
                updatedCategories[categoryId] = {};
              }
              // Conserve les données existantes jusqu'au nouveau maximum
              // Ajoute seulement les niveaux manquants (sans écraser les existants)
              for (let i = 1; i <= numValue; i++) {
                const levelKey = String(i);
                // Ne crée un nouveau niveau vide que s'il n'existe pas déjà
                if (!(levelKey in updatedCategories[categoryId])) {
                  updatedCategories[categoryId][levelKey] = "";
                }
              }
              // Supprime seulement les niveaux au-delà du nouveau maximum
              Object.keys(updatedCategories[categoryId]).forEach((level) => {
                if (parseInt(level) > numValue) {
                  delete updatedCategories[categoryId][level];
                }
              });
            });
            // Update labels - conserve les données existantes
            for (let i = 1; i <= numValue; i++) {
              const levelKey = String(i);
              // Ne crée un nouveau label vide que s'il n'existe pas déjà
              if (!(levelKey in updatedLabels)) {
                updatedLabels[levelKey] = "";
              }
            }
            // Supprime seulement les labels au-delà du nouveau maximum
            Object.keys(updatedLabels).forEach((level) => {
              if (parseInt(level) > numValue) {
                delete updatedLabels[level];
              }
            });
          } else {
            // If needsLevels is null, clear all level data
            values.selectedCategories.forEach((categoryId) => {
              if (updatedCategories[categoryId]) {
                updatedCategories[categoryId] = {};
              }
            });
            Object.keys(updatedLabels).forEach((level) => {
              delete updatedLabels[level];
            });
          }
          setFieldValue("categories", updatedCategories);
          setFieldValue("labels", updatedLabels);
        };

        const selectedCategoryObjects = coverageCategories.filter((cat) =>
          values.selectedCategories.includes(cat.id),
        );

        return (
          <Card
            type="simple"
            head={false}
            title={FORM.NEEDS_CONFIG || "Configuration des besoins"}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form>
                  {/* Bloc 1: Configuration des niveaux */}
                  <Col lg={24}>
                    <Divider orientation="left">
                      {FORM.LEVELS_CONFIG || "Configuration des niveaux"}
                    </Divider>
                  </Col>
                  <Col lg={8}>
                    <Input
                      type="number"
                      placeholder={FORM.NEEDS_LEVELS || "Nombre de niveaux"}
                      label={FORM.NEEDS_LEVELS || "Nombre de niveaux"}
                      min={1}
                      value={values.needsLevels ?? ""}
                      errors={get(formProps, "errors.needsLevels", "")}
                      touched={get(formProps, "touched.needsLevels", false)}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        handleNeedsLevelsInputChange(
                          inputValue === "" ? null : inputValue,
                        );
                      }}
                      onBlur={handleNeedsLevelsBlur}
                    />
                  </Col>
                  {values.needsLevels && (
                    <Col lg={24}>
                      <Row gutter={[16, 16]}>
                        {Array.from({ length: values.needsLevels }, (_, i) => {
                          const level = String(i + 1);
                          return (
                            <Col lg={8} key={`label-${level}`}>
                              <Input
                                type="simple"
                                placeholder={`${
                                  FORM.LEVEL_LABEL || "Label du niveau"
                                } ${level}`}
                                label={`${FORM.LEVEL || "Niveau"} ${level}`}
                                {...bindInputProps({
                                  name: `labels.${level}`,
                                  values,
                                  ...formProps,
                                })}
                              />
                            </Col>
                          );
                        })}
                      </Row>
                    </Col>
                  )}

                  {/* Bloc 2: Configuration des catégories */}
                  <Col lg={24}>
                    <Divider orientation="left">
                      {FORM.CATEGORIES_CONFIG || "Configuration des catégories"}
                    </Divider>
                  </Col>
                  <Col lg={12}>
                    <Select
                      mode="multiple"
                      options={coverageCategories.map((cat) => ({
                        label: cat.label,
                        value: cat.id,
                      }))}
                      placeholder={
                        FORM.SELECT_CATEGORIES || "Sélectionner les catégories"
                      }
                      label={
                        FORM.SELECT_CATEGORIES || "Catégories de couverture"
                      }
                      value={values.selectedCategories}
                      onChange={(selectedIds) => {
                        handleCategoriesChange(selectedIds);
                      }}
                    />
                  </Col>
                  <Col lg={24}>
                    <Input
                      type="checkbox"
                      placeholder={
                        FORM.PERSONALIZE_TEXT || "Personnaliser le texte"
                      }
                      label={FORM.PERSONALIZE_TEXT || "Personnaliser le texte"}
                      {...bindInputProps({
                        name: "personalizeText",
                        values,
                        ...formProps,
                      })}
                      checked={values.personalizeText}
                    />
                  </Col>

                  {/* Bloc 3: Textes personnalisés par catégorie */}
                  {values.personalizeText &&
                    values.selectedCategories.length > 0 &&
                    values.needsLevels && (
                      <>
                        <Col lg={24}>
                          <Divider orientation="left">
                            {FORM.PERSONALIZED_TEXTS ||
                              "Textes personnalisés par catégorie"}
                          </Divider>
                        </Col>
                        {selectedCategoryObjects.map((category) => (
                          <Col lg={24} key={category.id}>
                            <h3 className="mb-16">{category.label}</h3>
                            <Row gutter={[16, 16]}>
                              {Array.from(
                                { length: values.needsLevels },
                                (_, i) => {
                                  const level = String(i + 1);
                                  return (
                                    <Col lg={8} key={`${category.id}-${level}`}>
                                      <Input
                                        type="simple"
                                        placeholder={`${
                                          FORM.LEVEL_TEXT ||
                                          "Texte pour le niveau"
                                        } ${level}`}
                                        label={`${
                                          FORM.LEVEL || "Niveau"
                                        } ${level}`}
                                        {...bindInputProps({
                                          name: `categories.${category.id}.${level}`,
                                          values,
                                          ...formProps,
                                        })}
                                      />
                                    </Col>
                                  );
                                },
                              )}
                            </Row>
                          </Col>
                        ))}
                      </>
                    )}
                </Form>
              </Col>
            </Row>
          </Card>
        );
      }}
    </Formik>
  );
};

export default Needs;
